import { useEffect, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import { refreshToken } from "../Context/RefreshToken";

export const useAutoTokenRefresh = ({ navigate, checkIntervalMs = 5000, refreshMarginMs = 30000 } = {}) => {
  const timerRef = useRef(null);
  const isRefreshingRef = useRef(false);
  const refreshLoop = async () => {
    if (isRefreshingRef.current) return; // prevent overlapping calls
    isRefreshingRef.current = true;

    try {
      const accessToken = localStorage.getItem("digital-form-accesstoken");
      let needsRefresh = true;

      if (accessToken) {
        try {
          const decoded = jwtDecode(accessToken);
          const expTime = decoded.exp * 1000; // JWT exp is in seconds
          const now = Date.now();

          // Refresh only if token is expired or will expire soon (refreshMarginMs)
          needsRefresh = now >= expTime - refreshMarginMs;
        } catch (err) {
          console.warn("Invalid JWT detected, will attempt refresh.", err);
          needsRefresh = true; // invalid token → refresh
        }
      }

      if (needsRefresh) {
        await refreshToken({
          navigate,
          onSuccess: (token) => {
            if (token?.access_token && token?.refresh_token) {
              const newExpiry = Date.now() + token.expires_in * 1000;
              localStorage.setItem("digital-form-accesstoken", token.access_token);
              localStorage.setItem("digital-form-idtoken", token.id_token);
              localStorage.setItem("digital-form-refresh_token", token.refresh_token);
              localStorage.setItem("digital-form-expiry", newExpiry);
            }
          },
          onError: (err) => console.error("Failed to refresh token", err),
          autoReload: false,
        });
      } else {
        //
      }
    } catch (err) {
      console.error("Auto token refresh error:", err);
    } finally {
      isRefreshingRef.current = false;
    }
  };

  useEffect(() => {
    // Run once immediately
    refreshLoop();

    // Check token every checkIntervalMs
    timerRef.current = setInterval(refreshLoop, checkIntervalMs);

    return () => clearInterval(timerRef.current);
  }, []); // no dependencies

  return { triggerRefresh: refreshLoop };
};
